import { Box, Button, IconButton, Tooltip, Typography } from '@mui/material'
import { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { BaseMenu, BreadCrumb, CustomTable, SnackSimple } from '../../../../components'
import { DinamicForm } from '../../../../components/dinamicForm'
import { FiltersDrawer } from '../../../../components/filtersDrawer'
import Loader from '../../../../components/loader'
import { useContextState } from '../../../../hooks/theme'
import { icons } from '../../../../utils/icons'
import { Item, onChange } from '../../../../utils/types/generals'
import { ItemsInput, useColumn, useInput } from './initialStates'
import { getAll } from './methods'
import { useStyles } from './styles'
import { actionPlansServices } from '../../../../service/actionPlans'
import { httpFetch } from '../../../../utils/crud'
import { FilterProps } from '../../../../hooks/filter/typing'
import { getDivisions, getQuestionnaires, getStore, getUserTypes, getUsers, getZones } from '../../../../utils/methods/geServiceWithItemsFormat'
import { RequestErrors, Roles, Types } from '../../../../utils/enums'
import { returnQuery, returnQueryWithPagination } from '../../../../utils/methods/returnQuery'
import { handleAllErrors } from '../../../../utils/methods/handleErrors'
import { ModelDownloadButtons } from '../../../../components/donwloadFiles/model'
import { useDownloadWithUuid } from '../../../../hooks/download/useDpwnloadWithUuid'
import { formData, formatFilter, menuItems } from './constants'
import { useTimeOut } from '../../../../hooks/timeOut'
import SimpleModal from '../../../../components/modal/simpleModal'
import { ActionPlanContext } from '../../context'
import { questionnaire } from '../../../../utils/constants/callService'

const PlansToGenerate = () => {
  const { executeFunction } = useTimeOut()
  const { t } = useTranslation()
  const { colors } = useContextState()
  const [itemsSearch, setItemsSearch] = useState<ItemsInput>({})
  const { filterData, setFilterPayload, isChecked, notSelected, selectedIds, setIsChecked, setNotSelected, setSelectedIds } = useContext(ActionPlanContext)
  const isQuestionDisabled = !filterData.filterData.questionnaire
  const inputs = useInput({ ...itemsSearch, questionDisabled: isQuestionDisabled })
  const [rows, setRows] = useState<any[]>([])
  const styles = useStyles()
  const [loadingTable, setLoadingTable] = useState(false)
  const externalValidation: FilterProps['externalValidation'] = ({ body }) => {
    return body
  }
  const queryWithPagination = returnQueryWithPagination({ filter: filterData.filterString, sort: filterData.query.sort })
  const [tableDate, setTableDate] = useState({
    totalPages: 1,
    totalItems: 0,
    currentPages: 1,
  })

  const onChange: onChange = (e, blur) => {
    filterData.onChange(e, blur)
  }

  const cleanFilter = () => {
    filterData.cleanFilter()
  }

  const [isLoadingUndo, setIsLoadingUndo] = useState(false)
  const [uuid, setUuid] = useState('')

  const [snackbarOpen, setSnackbarOpen] = useState({
    open: false,
    message: '',
    type: Types.SUCCESS,
  })

  const [modal, setModal] = useState<{
    open: boolean
    title: string
    description?: string
    children?: JSX.Element
    removeCancel?: boolean
    confirm: () => void
  }>({
    open: false,
    title: t('general.confirmation'),
    description: '',
    confirm: () => { },
  })

  const getData = useCallback(async (data?: { queries?: string; reloadData?: boolean }) => {
    setLoadingTable(true)
    getAll(t, { ...data, queries: `${!!data?.queries ? `&${data.queries}` : ''}` })
      .then((response) => {
        const { row, currentPage, totalItems, totalPages } = response
        setTableDate({
          currentPages: currentPage,
          totalItems,
          totalPages,
        })
        setRows(row)
      })
      .catch(() => {
        setSnackbarOpen(prev => ({
          ...prev,
          open: true,
          message: t('actionPlan.snackbar.error.loadPlans'),
          type: Types.ERROR
        }))
        setRows([])
        setTableDate({
          currentPages: 1,
          totalItems: 0,
          totalPages: 1,
        })
      })
      .finally(() => {
        setLoadingTable(false)
      })
  }, [])

  const getDivision = async () => {
    const divisions = await getDivisions()
    setItemsSearch((prev) => ({
      ...prev,
      divisions: divisions.items,
    }))
  }

  const getStoreService = async () => {
    const { items } = await getStore()
    setItemsSearch((prev) => ({
      ...prev,
      stores: items,
    }))
  }

  const getUserService = async () => {
    const { items } = await getUsers({
      skip: [Roles.ADMIN],
      addUserLogged: true,
    });
    setItemsSearch((prev) => ({
      ...prev,
      user: items,
    }))
  }

  const getUserTypeService = async () => {
    const { items } = await getUserTypes()
    setItemsSearch((prev) => ({
      ...prev,
      userType: items,
    }))
  }

  const getQuestionnaire = async () => {
    const questionnaire = await getQuestionnaires()
    setItemsSearch((prev) => ({
      ...prev,
      questionnaire: questionnaire.items,
    }))
  }

  const getZonesService = async () => {
    const questionnaire = await getZones()
    setItemsSearch((prev) => ({
      ...prev,
      zone: questionnaire.fatherZones,
    }))
  }

  const handleOpenModalUndo = () => {
    if (selectedIds.length === 0 && !isChecked) {
      setSnackbarOpen(prev => ({
        ...prev,
        open: true,
        message: t('actionPlan.snackbar.selectAtLeastOnePlan'),
        type: Types.ERROR
      }))
    } else {
      setModal(prev => ({
        ...prev,
        open: true,
        title: t('actionPlan.areYouSureWantUndoAllPlansSelected'),
        description: t('actionPlan.toUndoTheSelectedPlans'),
        confirm: () => { handleUndo() },
      }))
    }
  }

  const closeModalUndo = () => {
    setModal(prev => ({
      ...prev,
      open: false,
    }))
  }

  const closeSnackBarOpen = () => {
    setSnackbarOpen(prev => ({ ...prev, open: false }))
  }

  const handleUndo = async () => {
    setIsLoadingUndo(true)

    const payload = {
      all: isChecked,
      actionPlanIds: isChecked ? [] : selectedIds,
      notIds: isChecked ? notSelected : []
    }

    const response = await actionPlansServices(httpFetch).deleteAutomaticTempMasive(
      payload,
      isChecked ? returnQuery({ ...filterData.query, filter: filterData.query.filter?.split('+').join('%2B') }, formData) : undefined
    )

    if (response.code == '00') {
      setSnackbarOpen(prev => ({
        ...prev,
        open: true,
        message: t('actionPlan.snackbar.undo.success'),
        type: Types.SUCCESS
      }))
      setSelectedIds([])
      setNotSelected([])
      setIsChecked(false)
      getData({ queries: returnQuery(filterData.query, formData) })
    } else {
      setSnackbarOpen(prev => ({
        ...prev,
        open: true,
        message: handleAllErrors(response?.code as RequestErrors, t),
        type: Types.ERROR
      }))
    }

    setIsLoadingUndo(false)
    closeModalUndo()
  }

  const handleDeleteUnique = async () => {
    setIsLoadingUndo(true)

    const response = await actionPlansServices(httpFetch).deleteAutomaticTemp(Number(detail.id))

    if (response.code == '01') {
      setSnackbarOpen(prev => ({
        ...prev,
        open: true,
        message: t('actionPlan.snackbar.delete.success.unique'),
        type: Types.SUCCESS
      }))
      setSelectedIds([])
      setNotSelected([])
      setIsChecked(false)
      getData({ queries: returnQuery(filterData.query, formData) })
    } else {
      setSnackbarOpen(prev => ({
        ...prev,
        open: true,
        message: handleAllErrors(response?.code as RequestErrors, t),
        type: Types.ERROR
      }))
    }

    setIsLoadingUndo(false)
    closeModalUndo()
  }

  const handleDeleteUniqueOpen = () => {
    setModal(prev => ({
      ...prev,
      open: true,
      title: t('general.confirmation'),
      description: t('actionPlan.areYouSureWantDeleteThisPlanSelected'),
      confirm: () => { handleDeleteUnique() },
    }))
  }

  const { column, closeMenu, detail, optionMenu } = useColumn(t, rows?.length, setIsChecked, selectedIds, isChecked, setSelectedIds, notSelected, setNotSelected, handleDeleteUniqueOpen)

  const {
    download,
    openSnackbar: snackbarDownloadModal,
    downloadCSV,
    downloadPDF,
    downloadWORD,
    closeOpenSnakbar,
    getDetailInform,
  } = useDownloadWithUuid({
    docName: 'Planes por generar',
    query: `${queryWithPagination ?? ''}`,
    uuid,
    setUuid,
    getUuid: actionPlansServices(httpFetch).getAutomaticTempRegister,
    downloadFuntion: actionPlansServices(httpFetch).getAutomaticTempGeneratedReport,
  })

  useLayoutEffect(() => {
    getDivision()
    getStoreService()
    getUserService()
    getQuestionnaire()
    getZonesService()
    getUserTypeService()
  }, [])

  useEffect(() => {
    const selectedQuestionnaire = filterData.filterData.questionnaire as Item | null

    if (!selectedQuestionnaire) {
      setItemsSearch((prev) => ({ ...prev, question: [] }))
      filterData.onChange({ target: { name: 'question', value: null } })
      return
    }

    const loadQuestions = async () => {
      const response = await questionnaire.getQuestionsByQuestionnaireId(selectedQuestionnaire.value)
      if (!response?.errors && Array.isArray(response)) {
        const formattedQuestions = response.map((q: { id: number; title: string }) => ({
          label: q.title,
          value: q.id,
        }))

        setItemsSearch((prev) => ({ ...prev, question: formattedQuestions }))

        const selectedQuestions = filterData.filterData.question as Item[] | undefined
        if (selectedQuestions?.length) {
          const availableIds = new Set(formattedQuestions.map((q) => String(q.value)))
          const newSelected = selectedQuestions.filter((q) => availableIds.has(String(q.value)))
          if (newSelected.length !== selectedQuestions.length) {
            filterData.onChange({ target: { name: 'question', value: newSelected.length ? newSelected : null } })
          }
        }
      }
    }

    loadQuestions()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterData.filterData.questionnaire])

  useEffect(() => {
    executeFunction({
      execute: () => {
        getData({ queries: returnQuery(filterData.query, formData) })
        setSelectedIds([])
        setNotSelected([])
        setIsChecked(false)
      }
    })
  }, [filterData.query.all])

  const getDetailInformRef = useRef(getDetailInform)
  getDetailInformRef.current = getDetailInform

  useEffect(() => {
    if (!uuid) return

    const interval = setInterval(() => {
      getDetailInformRef.current()
    }, 10000)

    const timeout = setTimeout(() => {
      clearInterval(interval)
      setUuid('')
      setSnackbarOpen(prev => ({
        ...prev,
        open: true,
        message: t('actionPlan.snackbar.download.timeout'),
        type: Types.ERROR
      }))
    }, 120000)

    return () => {
      clearInterval(interval)
      clearTimeout(timeout)
    }
  }, [uuid])

  useLayoutEffect(() => {
    setFilterPayload({
      formatFilter,
      externalValidation
    })
  }, [])

  return (
    <Box sx={styles.container}>
      <SnackSimple close={closeOpenSnakbar} open={snackbarDownloadModal.open} type={snackbarDownloadModal.type} message={snackbarDownloadModal.message} />
      <BreadCrumb
        removeBreadcrumb={['created', 'to-generate']}
        replacePath={{ 'action-plan': '/dashboard/action-plan/to-generate' }}
        replaceLabels={{ 'action-plan': 'actionPlanToGenerate' }}
      />
      <Box sx={[styles.header]}>
        <Typography variant='title_grey_big'>{t('actionPlan.titleAutomatic')}</Typography>

        <Box sx={styles.flex}>
          <Button variant="outlined" color="primary" onClick={handleOpenModalUndo}>
            {t('actionPlan.undo')}
          </Button>

          <ModelDownloadButtons styleType csv={downloadCSV} pdf={downloadPDF} word={downloadWORD} disableTypes={['pdf', 'word']} disableCSV={download} disablePDF={false} disableWORD={false} />

          {!!filterData.counter &&
            <Tooltip title={t('ToolTips.clearFilters')}>
              <IconButton
                sx={(styles.clean, (filterData.counter || 0) > 0 ? null : styles.hide)}
                onClick={() => {
                  cleanFilter()
                }}
              >
                {icons('clean')}
              </IconButton>
            </Tooltip>
          }
        </Box>
      </Box>

      <FiltersDrawer cleanFilter={cleanFilter} count={filterData.counter} right={0} top={50}>
        <Box sx={styles.section}>
          <DinamicForm errors={filterData.errors} input={Object.values(inputs)} values={filterData.filterData} onChange={onChange} filter={true} />
        </Box>
      </FiltersDrawer>

      <BaseMenu
        sx={[styles.baseMenu, detail.positionMenu, { '& .MuiPaper-root': { marginLeft: '-50px' } }]}
        anchorEl={detail.anchor}
        setAnchorEl={closeMenu}
        menuItems={detail.menu}
        click={(item) => {
          optionMenu({ item: item as Item })
        }}
      />

      {!loadingTable ? (
        <CustomTable
          order={
            filterData.query?.sort && {
              count: filterData.query?.sort?.count,
              key: filterData.query?.sort?.field,
              order: filterData.query?.sort?.sort,
            }
          }
          sortable={(field, sort, count) => {
            filterData.sortTable(field && sort ? { field: field, sort: sort, count } : undefined)
          }}
          serverSidePagination={{
            page: (filterData.paginationModel.page ?? 0) + 1,
            size: filterData.paginationModel?.pageSize ?? 20,
            totalPages: tableDate.totalPages,
          }}
          handleChange={(page: number, pageSize: number) => {
            filterData.handlePagination({ page, pageSize })
          }}
          alignStatus="start"
          column={column}
          row={rows}
          color={colors}
          selected={-1}
        />
      ) : (
        <Loader sx={{ height: 'calc(100% - 200px)' }} />
      )}

      <SnackSimple open={snackbarOpen.open} close={closeSnackBarOpen} message={snackbarOpen.message} type={snackbarOpen.type} />

      <SimpleModal
        open={!!modal.open}
        next={modal.confirm}
        back={closeModalUndo}
        close={closeModalUndo}
        description={modal.description}
        buttonText={t('general.confirm')}
        cancelText={t('general.cancel')}
        title={modal.title}
        loadingButton
        isLoading={isLoadingUndo}
      />
    </Box>
  )
}

export default PlansToGenerate
